import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, EmptyState, Segmented, Skeleton, useToast } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { emptyTexts } from '../../../shell/navigation';
import { ControllingTabs } from '../ControllingTabs';
import { ScopeMenu } from '../ScopeMenu';
import { CampaignsTable } from './CampaignsTable';
import { Funnel } from './Funnel';
import { NotarizedTile, PerPerson } from './PerPerson';
import { ReferrerPartnersTable } from './ReferrerPartnersTable';
import {
  downloadFigures,
  isFiguresPeriod,
  isFiguresScope,
  useFigures,
  type ControllingFigures,
  type FiguresPeriod,
  type FiguresScope,
} from './api';
import styles from './figures.module.css';

/**
 * Controlling 04 „Kennzahlen" (`/controlling?period=&scope=`, tab „Kennzahlen" of `ControllingTabs`, D-324): the funnel with the previous-period markers, the
 * campaigns, the referrer partners (D-248), the „Beurkundet" tile and „Je Person". Period and scope live in the URL; without `scope` the server
 * picks the widest scope of the viewer (team → organization → me). The scope menu appears only when the server
 * offers more than one scope.
 */

export const PERIOD_ITEMS: { value: FiguresPeriod; label: string }[] = [
  { value: 'week', label: 'Woche' },
  { value: 'month', label: 'Monat' },
  { value: 'quarter', label: 'Quartal' },
];

function FiguresSkeleton() {
  return (
    <div className={styles.grid} aria-busy="true" aria-label="Wird geladen">
      <div className={styles.column}>
        <div className={clsx(styles.card, styles.funnelCard)}>
          <div className={styles.skeletonStack}>
            <Skeleton width="36%" height={18} />
            {Array.from({ length: 7 }, (_, index) => (
              <Skeleton key={index} variant="block" height={22} />
            ))}
          </div>
        </div>
        <div className={clsx(styles.card, styles.funnelCard)}>
          <div className={styles.skeletonStack}>
            <Skeleton width="24%" height={18} />
            <Skeleton variant="block" height={40} />
            <Skeleton variant="block" height={40} />
          </div>
        </div>
      </div>
      <div className={styles.column}>
        <div className={clsx(styles.card, styles.sideCard)}>
          <div className={styles.skeletonStack}>
            <Skeleton width="30%" height={10} />
            <Skeleton width="60%" height={30} />
          </div>
        </div>
        <div className={clsx(styles.card, styles.sideCard)}>
          <div className={styles.skeletonStack}>
            <Skeleton width="30%" height={10} />
            <Skeleton variant="block" height={30} />
            <Skeleton variant="block" height={30} />
          </div>
        </div>
      </div>
    </div>
  );
}

function hasData(data: ControllingFigures): boolean {
  return (
    data.funnel.some((row) => row.value > 0) ||
    data.campaigns.length > 0 ||
    data.perPerson.length > 0 ||
    (data.referrerPartners ?? []).length > 0
  );
}

export function FiguresPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [search, setSearch] = useSearchParams();
  const periodParam = search.get('period');
  const scopeParam = search.get('scope');
  const period: FiguresPeriod = isFiguresPeriod(periodParam) ? periodParam : 'month';
  const scope: FiguresScope | null = isFiguresScope(scopeParam) ? scopeParam : null;
  const figures = useFigures(period, scope);
  const [exporting, setExporting] = useState(false);

  function setParam(key: 'period' | 'scope', value: string) {
    setSearch(
      (current) => {
        const params = new URLSearchParams(current);
        params.set(key, value);
        return params;
      },
      { replace: true },
    );
  }

  async function exportCsv() {
    if (exporting) return;
    setExporting(true);
    try {
      await downloadFigures(period, scope ?? figures.data?.scope ?? null);
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setExporting(false);
    }
  }

  const data = figures.data;
  const forbidden = figures.error instanceof ApiError && figures.error.status === 403;
  const empty = emptyTexts.controlling;

  if (forbidden) {
    return (
      <div className={styles.page}>
        <ControllingTabs tab="figures" />
        <EmptyState
          eyebrow="Controlling"
          title="Kennzahlen"
          description={errorMessage(figures.error)}
          actions={
            <Button variant="secondary" onClick={() => void navigate('/tasks')}>
              {empty.primary.label}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className={styles.page} aria-busy={figures.isFetching ? true : undefined}>
      <div className={styles.head}>
        <div className={styles.headText}>
          <div className={styles.eyebrow}>Controlling</div>
          <h1 className={styles.title}>{data?.header.title ?? 'Kennzahlen'}</h1>
          <div className={styles.line}>{data ? data.header.line : <Skeleton width={320} height={12} />}</div>
        </div>
        <div className={styles.headActions}>
          <Segmented
            label="Zeitraum"
            items={PERIOD_ITEMS}
            value={period}
            onChange={(value) => setParam('period', value)}
          />
          {data ? (
            <ScopeMenu
              scopes={data.scopes}
              scope={data.scope}
              onChange={(value) => setParam('scope', value)}
            />
          ) : null}
          <Button variant="secondary" loading={exporting} disabled={!data} onClick={() => void exportCsv()}>
            Export
          </Button>
        </div>
      </div>
      <ControllingTabs tab="figures" />

      {figures.isError && !data ? (
        <Banner
          tone="danger"
          actions={
            <Button variant="secondary" size="small" onClick={() => void figures.refetch()}>
              Erneut laden
            </Button>
          }
        >
          {errorMessage(figures.error)}
        </Banner>
      ) : null}

      {figures.isPending ? <FiguresSkeleton /> : null}

      {data && !hasData(data) ? (
        <EmptyState
          eyebrow={empty.eyebrow}
          title={empty.headline}
          description={empty.description}
          actions={
            <>
              <Button onClick={() => void navigate(empty.primary.href)}>{empty.primary.label}</Button>
              <Button variant="secondary" onClick={() => void navigate(empty.secondary.href)}>
                {empty.secondary.label}
              </Button>
            </>
          }
        />
      ) : null}

      {data && hasData(data) ? (
        <div className={styles.grid}>
          <div className={styles.column}>
            <Funnel rows={data.funnel} />
            <CampaignsTable rows={data.campaigns} />
            <ReferrerPartnersTable rows={data.referrerPartners ?? []} />
          </div>
          <div className={styles.column}>
            <NotarizedTile notarized={data.notarized} />
            <PerPerson rows={data.perPerson} />
            <div className={styles.footnote}>{data.footnote}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
